import CachedInstance from "./CachedInstance";
import CyclingCacheListener from "./CyclingCacheListener";

/**
 * @abstract
 * @implements {CyclingCacheListener}
 */
export default class CyclingCache {
    constructor() {
        /**
         * @type {Array.<CachedInstance>}
         */
        this.cache = [];
        /**
         * @type {number}
         */
        this.index = 0;
    
        /**
         * @type {number}
         */
        this.totalSize = 0; // this is only updated in the resetCache() method.
        /**
         * @type {number}
         */
        this.count = 0; // this is only updated in the resetCache() method.
    
        /**
         * @type {number}
         */
        this.currentActiveCount = 0;
        /**
         * @type {number}
         */
        this.maxActiveCount = 0;
    }

    /**
     * @returns {CachedInstance}
     */
    get() {
        if (this.cache.length == 0) {
            this.growCache(this.getInitialSize());
        }

        if (this.index >= this.cache.length) {
            this.index = 0;
        }

        // Skip past elements in use.
        this.index = this.findNextAvailableIndex(this.index);

        const c = this.cache[this.index];

        this.index++;

        // Reset and mark as not finished.
        c.reset(false);

        return c;
    }

    /**
     * @private
     * @param {number} index 
     * @returns {number}
     */
    findNextAvailableIndex(index) {

        let size = this.cache.length;

        // If the cache is full then we will need to grow it.
        if (this.maxActiveCount >= size) {
            // Move the index to where the available instances will be
            index = size;
            // Grow the cache
            this.growCache(this.getGrowAmount());
            size = this.cache.length;
        }

        // If we wrap around the entire list and fail to find anything, then we need to expand the cache.
        let wrapCount = 0;
        do {
            if (index >= size) {
                wrapCount++;

                // The first time we hit the end of the list, we wrap around to the start.
                if (wrapCount == 1) {
                    index = 0;
                }
                else {
                    // We have failed to find an available action. Grow the cache.
                    //Log.info(getName() + ": growCache() suspicious cache growth. maxActiveCount=" + maxActiveCount + " size=" + size);
                    this.growCache(this.getGrowAmount());
                    size = this.cache.length;
                }
            }

            const c = this.cache[index];

            // If available, then we are done.
            if (c.isFinishedAndAvailable()) {
                return index;
            }
            else {
                index++;
            }
        }
        while (wrapCount < 2); // this used to be 'while(true)', but lint didn't like it.
    }

    /**
     * @returns {Array.<CachedInstance>}
     */
    getUnderlyingCache() {
        return this.cache;
    }
    
    /**
     * @private
     * @param {number} amount 
     */
    growCache(amount) {
        for (let i = 0; i < amount; i++) {
            const c = this.createInstance();
            c.setCycleCacheListener(this);
            this.cache.push(c);
        }

        console.log('CyclingCache.growCache() amount=' + amount + ' newSize=' + this.cache.length + ' Type: ' + this.constructor.name);
    }

    /**
     * @private
     * @param {number} newSize 
     */
    shrinkToSize(newSize) {
        // cache.truncate(newSize);
        // cache.shrink();

        const excess = this.cache.length - newSize;
        if (excess > 0) {
            this.cache.splice(newSize, excess);
        }
    }


    resetCache() {
        const size = this.cache.length;

        // Don't do anything if the size is 0.
        if (size == 0) {
            return;
        }

        // Keep track of how big the cache is at this point.
        this.count++;
        this.totalSize += this.maxActiveCount;

        const average = this.totalSize / this.count;
        
        // Reset the contents of the cache.
        for (let i = 0; i < size; i++) {
            const c = this.cache[i];
            if (!c.isFinishedAndAvailable()) {
                c.reset(true);
            }
        }

        // If the cache is greater than the average (plus a buffer), then shrink it.
        // Make sure we have accumulated at least 5 resets worth of data before adjusting
        // the cache size.
        // Make sure you do this after we have reset all the cache contents.
        if (this.count > 5) {
            const growAmount = this.getGrowAmount();
            const minShrinkThreshold = this.getInitialSize() + (2 * growAmount);
            if (size > (average * 1.75) && size > minShrinkThreshold) {
                const shrinkTarget = (average * 1.2) | 0;
                let newSize = this.getInitialSize();
                while (newSize < shrinkTarget) {
                    newSize += growAmount;
                }

                if (newSize < size) {
                    this.shrinkToSize(newSize);
                }

                // start counting fresh
                this.totalSize = 0;
                this.count = 0;
            }
        }

        this.maxActiveCount = 0;
        this.currentActiveCount = 0;
        this.index = 0;
    }

    // public String getName() {
    //     return getClass().getSimpleName();
    // }

    /**
     * @abstract
     * @returns {number}
     */
    getGrowAmount() {}

    /**
     * @abstract
     * @returns {number}
     */
    getInitialSize() {}

    /**
     * @abstract
     * @returns {CachedInstance}
     */
    createInstance() {}

    // getLogSizeThreshold() {
    //     return Math.max(this.getGrowAmount() * 20, 1000);
    // }

    /**
     * @override
     * @param {boolean} finished 
     */
    onAvailabilityChanged(finished) {
        if (finished) {
            this.currentActiveCount--;
            // current active count should never be less than 0
        }
        else {
            this.currentActiveCount++;
            this.maxActiveCount = Math.max(this.maxActiveCount, this.currentActiveCount);
            // max active count should never be greater than the size of the cache.
        }

        //////console.log('CyclingCache. currentActiveCount=' + this.currentActiveCount + ' maxActiveCount=' + this.maxActiveCount);
    }

}